/* @flow weak */
import * as React from 'react';
import gql from 'graphql-tag';
import { graphql, compose } from 'react-apollo';
import { connect } from 'react-redux';
import { addBox, removeBox } from 'reducers/floatingBoxes';
import { bindActionCreators } from 'redux';
import PhoneCallFloatingBox from 'phoneCalls/components/PhoneCallFloatingBox';
import CurrentUser from 'auth/CurrentUser';
import { showToast } from 'reducers/toasts';
import type { State as PhoneCallsState } from 'phoneCalls/reducers/phoneCalls';
import { showRecent, hideRecent } from 'reducers/recentMessagesModal';
import PhoneCallInitializingView from 'phoneCalls/components/PhoneCallInitializingView';
import PhoneCallRetryView from 'phoneCalls/components/PhoneCallRetryView';
import Dialog from 'components/Dialog';

class PhoneCallStartedView extends React.Component<{ onMount: () => void }> {
  componentDidMount() {
    this.props.onMount();
  }
  render() {
    return null;
  }
}

type Props = {
  calleeUuid: string,
  calleeName: string,
  phoneCalls: PhoneCallsState,
  showToast: (text: string, kind: 'success' | 'error') => *,
  initiatePhoneCall: (callerUuid: string, calleeUuid: string) => Promise<*>,
  cancelPhoneCall: (phoneCallUuid: string) => Promise<*>,
  onRequestHide: Function,
  showRecent: Function,
  hideRecent: Function,
  addBox: Function,
  removeBox: Function,
};

type State = {
  uuid: ?string,
};

class PhoneCallModalContent extends React.Component<Props, State> {
  state: State = {
    uuid: null,
  };

  componentDidMount() {
    this.initializePhoneCall();
  }

  initializePhoneCall = () => {
    if (this.state.uuid) {
      this.setState({ uuid: null });
    }
    const callerUuid = CurrentUser.getUserUuid();
    this.props
      .initiatePhoneCall(callerUuid, this.props.calleeUuid)
      .then(data => this.setState({ uuid: data.uuid }), this.handleError)
      .catch(e => {
        console.error('PhoneCallModalContent initializePhoneCall error', e);
        this.props.showToast('There was a problem starting your phone call.', 'error');
      });
  };

  handleError = (error: *) => {
    console.error('initiate phone call failed', error);
    this.props.showToast(error.message, 'error');
    this.props.onRequestHide();
  };

  handleCallStarted = () => {
    this.props.onRequestHide();
    this.props.addBox(
      this.state.uuid,
      <PhoneCallFloatingBox
        uuid={this.state.uuid}
        remove={() => this.props.removeBox(this.state.uuid)}
      />
    );
  };

  callCancelled = () => {
    this.props.showToast(`Call with ${this.props.calleeName} cancelled!`, 'success');
    this.props.onRequestHide();
  };

  cancelCall = () => {
    if (!this.state.uuid) {
      this.callCancelled();
      return;
    }
    this.props
      .cancelPhoneCall(this.state.uuid)
      .then(this.callCancelled)
      .catch(err => this.props.showToast('Error, ' + err.message, 'error'));
  };

  cancelRetry = () => {
    this.props.onRequestHide();
  };

  render() {
    const call = this.state.uuid ? this.props.phoneCalls.get(this.state.uuid) : null;
    const status = call ? call.status : 'initializing';
    let content;
    let hero;
    let onDialogDismiss;
    let hideCloseButton = false;
    switch (status) {
      case 'initializing':
        content = (
          <PhoneCallInitializingView name={this.props.calleeName} onCancel={this.cancelCall} />
        );
        hero = '/assets/images/phoneCalls/PhoneCallModal/click-to-call-answer.svg';
        onDialogDismiss = this.cancelCall;
        hideCloseButton = true;
        break;
      case 'caller_unreachable':
        content = (
          <PhoneCallRetryView
            name={this.props.calleeName}
            onCancel={this.cancelRetry}
            onRetry={this.initializePhoneCall}
          />
        );
        hero = '/assets/images/phoneCalls/PhoneCallModal/click-to-call-didnt-pick-up.svg';
        break;
      case 'started':
        content = <PhoneCallStartedView onMount={this.handleCallStarted} />;
        break;
      default:
        console.error('PhoneCallModalContent - error rendering unsupported status', status);
        content = <span>status not supported</span>;
        break;
    }
    return (
      <Dialog
        eventScreenName="start_phone_call"
        hideOnEscape={false}
        hideOnBlur={false}
        hideCloseButton={hideCloseButton}
        onRequestHide={this.props.onRequestHide}
        size="small"
        onDialogDismiss={onDialogDismiss}
        headerImage={hero || undefined}>
        {content}
      </Dialog>
    );
  }
}

function mapStateToProps(state: Object) {
  return {
    phoneCalls: state.phoneCalls,
  };
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ showToast, showRecent, hideRecent, addBox, removeBox }, dispatch);
}

const withMutations = graphql(
  gql`
    mutation clickToCall($input: InitiateCallInput!) {
      clickToCall(input: $input) {
        uuid
        error {
          message
        }
      }
    }
  `,
  {
    props({ mutate }) {
      return {
        initiatePhoneCall(callerUuid: string, calleeUuid: string) {
          const input = { callerUuid, calleeUuid };
          return mutate({ variables: { input } }).then(({ data: { clickToCall } }) => {
            if (clickToCall.error) {
              throw new Error(clickToCall.error.message);
            }
            return clickToCall;
          });
        },
      };
    },
  }
);

const withCancelMutations = graphql(
  gql`
    mutation cancelPhoneCall($input: CancelPhoneCallInput!) {
      cancelPhoneCall(input: $input) {
        error {
          code
          message
        }
      }
    }
  `,
  {
    props({ mutate }) {
      return {
        cancelPhoneCall(phoneCallUuid: string) {
          const input = { phoneCallUuid };
          return mutate({ variables: { input } }).then(({ data: { cancelPhoneCall } }) => {
            if (cancelPhoneCall.error) {
              throw new Error(cancelPhoneCall.error.message);
            }
            return cancelPhoneCall;
          });
        },
      };
    },
  }
);

export { PhoneCallModalContent };
export default compose(
  withCancelMutations,
  withMutations,
  connect(mapStateToProps, mapDispatchToProps)
)(PhoneCallModalContent);
